import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useStrategiesStore } from "@/stores/strategiesStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { StrategyWithVersions, StrategyVersion } from "@/lib/types";
import { api } from "@/lib/api";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import StrategyVersionForm from "@/components/StrategyVersionForm";
import { LANGUAGE_MAP, DEFAULT_LANGUAGE } from "@/pages/Strategies";
import { Copy, Edit2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";

const StrategyDetails = () => {
  const { strategyId } = useParams<{ strategyId: string }>();
  const [strategy, setStrategy] = useState<StrategyWithVersions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isEditingVersion, setIsEditingVersion] = useState<string | null>(null);
  const [editedVersionDesc, setEditedVersionDesc] = useState("");

  const { updateStrategy } = useStrategiesStore();
  const { selectedOrganizationId } = useOrganizationsStore();

  useEffect(() => {
    const fetchStrategy = async () => {
      if (!strategyId || !selectedOrganizationId) return;
      setIsLoading(true);
      try {
        const fetchedStrategy = await api.getStrategy(strategyId);
        setStrategy(fetchedStrategy);
        updateStrategy(selectedOrganizationId, strategyId, fetchedStrategy);
      } catch (err) {
        setError("Failed to fetch strategy details");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStrategy();
  }, [strategyId, selectedOrganizationId, updateStrategy]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!strategy) return <div>Strategy not found</div>;

  const handleRenameStrategy = async () => {
    if (!strategyId || !selectedOrganizationId || !editedName.trim()) return;
    
    try {
      const updatedStrategy = await api.renameStrategy(strategyId, editedName);
      setStrategy(updatedStrategy);
      updateStrategy(selectedOrganizationId, strategyId, updatedStrategy);
      setIsEditingName(false);
      toast({
        title: "Success",
        description: "Strategy renamed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to rename strategy",
        variant: "destructive",
      });
    }
  };

  const handleRenameVersion = async (versionId: string) => {
    if (!editedVersionDesc.trim()) return;
    
    try {
      const updatedVersion = await api.renameStrategyVersion(versionId, editedVersionDesc);
      setStrategy((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          versions: prev.versions.map((v) =>
            v.id === versionId ? { ...v, version_description: updatedVersion.version_description } : v
          ),
        };
      });
      setIsEditingVersion(null);
      toast({
        title: "Success",
        description: "Version description updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update version description",
        variant: "destructive",
      });
    }
  };

  const handleVersionCreated = (newVersion: StrategyVersion) => {
    setStrategy((prev) => {
      if (!prev) return null;
      return { ...prev, versions: [...prev.versions, newVersion] };
    });
    setIsDialogOpen(false);
  };

  const getDetail = (label: string, value: React.ReactNode) => (
    <p>
      <span className="uppercase text-xs font-bold text-gray-400 mr-2">
        {label}
      </span>
      {value}
    </p>
  );

  const copyVersionId = async (versionId: string) => {
    try {
      await navigator.clipboard.writeText(versionId);
      toast({
        title: "Success",
        description: "Version ID copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy Version ID",
        variant: "destructive",
      });
    }
  };

  // Sort versions by created_at date, newest first
  const sortedVersions = [...strategy.versions].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div className="p-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {isEditingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRenameStrategy();
                    if (e.key === "Escape") setIsEditingName(false);
                  }}
                  className="text-2xl font-semibold"
                  autoFocus
                />
                <Button size="sm" onClick={handleRenameStrategy}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditingName(false)}>Cancel</Button>
              </div>
            ) : (
              <>
                <CardTitle>{strategy.name}</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditedName(strategy.name);
                    setIsEditingName(true);
                  }}
                  className="h-8 w-8"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          <CardDescription>{strategy.description}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm flex justify-between">
          {getDetail("Call Type", <b>{strategy.call_type}</b>)}
          {getDetail("Medium", <b>{strategy.medium ?? "phone"}</b>)}
          {getDetail(
            "Created At",
            format(new Date(strategy.created_at), "MMMM d, yyyy h:mm a")
          )}
          {getDetail(
            "Language",
            <b>{LANGUAGE_MAP[strategy.language ?? DEFAULT_LANGUAGE]}</b>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between items-center mt-6 mb-4">
        <h2 className="text-xl font-bold">Strategy Versions</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>Create New Version</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Version</DialogTitle>
              <DialogDescription>
                Create a new version of this strategy.
              </DialogDescription>
            </DialogHeader>
            <StrategyVersionForm
              strategyId={strategy.id}
              onVersionCreated={handleVersionCreated}
              onCancel={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Accordion type="multiple" className="w-full">
        {sortedVersions.map((version) => (
          <AccordionItem
            key={version.id}
            value={version.id}
            className="border-b border-gray-200"
          >
            <AccordionTrigger className="hover:bg-gray-50 p-4 rounded-t-md">
              <div className="flex flex-col items-start text-left flex-1">
                <div className="flex items-center gap-2 w-full">
                  {isEditingVersion === version.id ? (
                    <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                      <Input
                        value={editedVersionDesc}
                        onChange={(e) => setEditedVersionDesc(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            handleRenameVersion(version.id);
                          }
                          if (e.key === "Escape") {
                            e.stopPropagation();
                            setIsEditingVersion(null);
                          }
                        }}
                        placeholder="Version description"
                        className="flex-1"
                        autoFocus
                      />
                      <Button size="sm" onClick={() => handleRenameVersion(version.id)}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setIsEditingVersion(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <>
                      <div className="font-medium text-md">
                        {version.version_description || `Version ${version.id}`}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditedVersionDesc(version.version_description || "");
                          setIsEditingVersion(version.id);
                        }}
                        className="h-8 w-8"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
                <div className="text-sm text-muted-foreground font-light">
                  <span>
                    Created:{" "}
                    {format(new Date(version.created_at), "MMM d, yyyy")}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="bg-gray-50 p-6 rounded-b-md">
              <div className="space-y-4">
                <div className="flex items-center mb-2">
                  <span className="uppercase text-xs font-bold text-gray-400 mr-2">
                    Version ID:
                  </span>
                  <span>{version.id}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyVersionId(version.id)}
                    className="h-8 w-8 ml-1"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-col">
                  <span className="uppercase text-xs font-bold text-gray-400 mb-2">
                    Prompt
                  </span>
                  <p className="whitespace-pre-wrap bg-white p-5 rounded border border-gray-100 text-sm font-light">
                    {version.prompt}
                  </p>
                </div>

                <div className="flex flex-col">
                  <span className="uppercase text-xs font-bold text-gray-400 mb-2">
                    Main System Prompt
                  </span>
                  <p className="whitespace-pre-wrap bg-white p-5 rounded border border-gray-100 text-sm font-light">
                    {version.main_system_prompt.prompt}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 font-light">
                  <div>
                    {getDetail(
                      "LLM Model",
                      <span>
                        {version.llm_model_provider}{" "}
                        <span className="text-gray-600">
                          {version.llm_model_name}
                        </span>
                      </span>
                    )}
                  </div>
                  <div>
                    {getDetail(
                      "Created At",
                      format(
                        new Date(version.created_at),
                        "MMMM d, yyyy h:mm a"
                      )
                    )}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default StrategyDetails;
